import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useRefreshMeetingContext } from 'Hooks/useRefreshEvent';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';

const defaultState = {
  meetingResource: null,
  loading: false,
  loadingError: null,
};

const MeetingContext = createContext(defaultState);

const MeetingContextProvider = ({ children }) => {
  const { householdResource } = useSimpleHousehold();
  const [meetingResource, setMeetingResource] = useState(defaultState.meetingResource);
  const [loading, setLoading] = useState(defaultState.loading);
  const [loadingError, setLoadingError] = useState(defaultState.loadingError);

  async function refreshMeetingResource() {
    if (!meetingResource) {
      return;
    }

    setLoading(true);

    try {
      const resp = await meetingResource.link('self').get();

      const [updatedResource] = resp;
      setMeetingResource(updatedResource);
    } catch (err) {
      setLoadingError(err);
    }

    setLoading(false);
  }

  useRefreshMeetingContext(() => {
    refreshMeetingResource();
  }, [meetingResource]);

  React.useEffect(() => {
    return () => {
      // Clean up the meeting context when you switch households
      setMeetingResource(defaultState.meetingResource);
      setLoading(defaultState.loading);
      setLoadingError(defaultState.loadingError);
    };
  }, [householdResource?.id()]);

  return (
    <MeetingContext.Provider
      value={{
        meetingLoading: loading,
        meetingLoadingError: loadingError,
        meetingResource,
        refreshMeetingResource,
        setMeetingResource,
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
};

export const useMeetingContext = () => {
  const context = useContext(MeetingContext);

  if (!context) throw new Error('useMeetingContext must be called from MeetingContext.Provider');

  return context;
};

MeetingContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

export default MeetingContextProvider;
